/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    CSSProperties,
} from 'react';
import _ from 'lodash';
import jszip from 'jszip';
import styled from 'styled-components';

import Config from 'Config';
import * as logger from 'common/utils/Logger';
import { getDownloadAllPhotoUrl as QuoteGetDownloadAllPhotoUrl } from 'admin/services/AdminQuotesService';
// eslint-disable-next-line import/no-cycle
import { getDownloadAllPhotoUrl as WordOrderGetDownloadAllPhotoUrl } from 'admin/services/AdminWorkOrdersService';
import {
    getPhotoMetadataByHashId,
    getUsagePhotoByDocIds,
} from 'admin/services/AdminPhotoService';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import DoneAll from 'images/svg/done_all.svg';
import ReviewModeIconImg from 'images/svg/review_mode_eye.svg';
import ImageSizeButton2 from 'images/svg/image_size_button_2.svg';
import ImageSizeButton2Active from 'images/svg/image_size_button_active_2.svg';
import ImageSizeButton3 from 'images/svg/image_size_button_3.svg';
import ImageSizeButton3Active from 'images/svg/image_size_button_active_3.svg';
import ImageSizeButton4 from 'images/svg/image_size_button_4.svg';
import ImageSizeButton4Active from 'images/svg/image_size_button_active_4.svg';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import checkedIcon from 'images/svg/checked.svg';
import {
    APPROVAL_STATUS,
    PhotoIntegrity,
    Document,
    Photo,
} from 'common/types/documentRequirements';
import { PhotoRequirement } from 'common/types';
import { Flex } from '@motili/ui';
import {
    isVideoMimeType,
    isPdfMimeType,
    isImageMimeType,
} from 'common/components/file-upload/FileUploadTypes';
import Checkbox from 'common/components/form/CheckboxWrapper';
// eslint-disable-next-line import/no-cycle
import {
    DocumentRequirementsPhotosEditModal,
    DocumentSuccessfullyDownloadAllPhotoModal as DocumentSuccessfullyDownloadAllPhotoModalLegacy,
} from '../DocumentRequirementsPhotosModal';
import { DocumentSuccessfullyDownloadAllPhotoModal } from '../DocumentSuccessfullyDownloadAllPhotoModal';
import { FormElement, FormRow } from '../../form/FormLayout';
import PhotoMetadataModal from '../../modal/PhotoMetadataModal';
import DocumentRequirementsRecommendationDrawer from '../DocumentRequirementsRecommendationDrawer';
import DocumentsPhotoModal from '../../modal/DocumentsPhotoModal';
import PhotoIntegrityFailReasons from '../../modal/PhotoIntegrityFailReasons';
import AdminBulkApprovalSideBar from '../../../../admin/components/work-orders/photoRequirements/AdminBulkApprovalSideBar';
import DocumentRequirementsPhotosUploadedRowContainer from './DocumentRequirementsPhotosUploadedRow/DocumentRequirementsPhotosUploadedRowContainer';
import { DownloadIconButton } from '../../buttons/ActionIconButtons';
import DocumentRequirementsPhotoSearchAndFilter, {
    ActiveFilterItem,
    FilterType,
} from './DocumentRequirementsPhotoSearchAndFilter';
import { AssetType } from './DocumentRequirementsPhotos';
import { useAppSelector } from '../../../stores/redux/hooks';
import DocumentRequirementsPhotosReviewModeModal, {
    checkShowApprovalRejectButton,
} from '../AdminDocumentRquirementsPhotos/AdminDocumentRequirementsPhotoReviewMode';
import { isRequirementMet } from '../AdminDocumentRquirementsPhotos/AdminDocumentRequirementsPhotos';

const MinCardWidth = 330;

const RequirementHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 18px 0 10px;
`;

const RequirementHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const RequirementTitle = styled.div`
    font-size: 20px;
    font-weight: 600;
`;

const ScrollFadeContainer = styled.div<{
    $fadeLeft?: boolean;
    $fadeRight?: boolean;
}>`
    position: relative;
    width: 100%;
    min-width: 0;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 80px;
        height: 100%;
        background: linear-gradient(to left, transparent, #fff);
        pointer-events: none;
        z-index: 1;
        opacity: ${({ $fadeLeft }) => ($fadeLeft ? 1 : 0)};
        transition: opacity 0.2s;
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 80px;
        height: 100%;
        background: linear-gradient(to right, transparent, #fff);
        pointer-events: none;
        z-index: 1;
        opacity: ${({ $fadeRight }) => ($fadeRight ? 1 : 0)};
        transition: opacity 0.2s;
    }
`;

const HeaderInfoIcon = styled(InfoOutlinedIcon)`
    cursor: pointer;
    font-size: 20px !important;
    color: ${Config.color.motiliLightBlue};
`;

const HeaderCheckIcon = () => (
    <img src={checkedIcon} alt='checked' style={{ width: 14, height: 14 }} />
);

const CardsRow = styled.div<{ $cols: number }>`
    width: 100%;
    min-width: 0;
    display: flex;
    gap: 16px;
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    padding: 8px 0 12px;

    scrollbar-width: initial !important;
    scrollbar-color: initial !important;

    &::-webkit-scrollbar {
        height: 8px !important;
        display: block;
    }

    &::-webkit-scrollbar-track {
        border-radius: 20px !important;
    }

    &::-webkit-scrollbar-thumb {
        border: 1px solid transparent;
        background: rgba(59, 59, 59, 0.5) !important;
        background-clip: content-box;
        border-radius: 20px !important;
        transition: all 0.5s ease-in-out !important;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: rgba(59, 59, 59, 0.8) !important;
        border: 0 solid transparent;
    }
`;

const CardItem = styled.div<{ $cardW: number; $cardH?: number }>`
    flex: 0 0 ${({ $cardW }) => $cardW}px;
    width: ${({ $cardW }) => $cardW}px;
    ${({ $cardH }) => ($cardH ? `height:${$cardH}px;` : '')}
`;

const EmptyCardItem = styled.div`
    flex: 0 0 100%;
    width: 100%;
    display: flex;
    justify-content: center;
`;

const BulkApproveBar = styled.div`
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 2px;
    padding-left: 0px;
`;

const UploadedPhotosLabel = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin-bottom: 0;
    align-self: flex-end;
`;

const SelectAllLabel = styled.label`
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    color: #333333;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 0px;
`;

const BulkApproveLeft = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 16px;
`;

const BulkApproveSpacer = styled.span`
    width: 12px;
`;

const BulkApproveAction = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    color: ${Config.color.motiliLightBlue};
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const BulkApproveIcon = styled.img`
    width: 18px;
    height: 18px;
    display: inline-block;
`;

const LayoutToggleWrap = styled.div`
    display: inline-flex;
    gap: 8px;
`;

const LayoutBtn = styled.button<{ active?: boolean }>`
    width: 40px;
    height: 36px;
    border: none;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    img {
        width: 40px;
        height: 36px;
        display: block;
    }
`;

const ReviewModeBtn = styled.button<{ active?: boolean }>`
    width: 42px;
    height: 38px;
    border-radius: 4px;
    border: 1px solid #d9d9d9;
    background: ${({ active }) => (active ? '#0b1f4b' : '#fff')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        border-color: #0b1f4b;
    }

    img {
        width: 18px;
        height: 18px;
        object-fit: contain;
    }
`;

const SeparateText = styled.span`
    color: ${Config.color.localGray};
    &::before {
        content: '|';
    }
`;

const MEDIA_FILTER = Object.freeze({
    ALL: 'ALL',
    PHOTOS: 'PHOTOS',
    VIDEOS: 'VIDEOS',
    DOCUMENTS: 'DOCUMENTS',
});

const FILTER_ALL = 'ALL';

enum MEDIA_STATUS_TYPE {
    ACCEPT = 'ACCEPT',
    APPROVE = 'APPROVE',
    REJECT = 'REJECT',
}

type MEDIA_FILTER_TYPE = 'ALL' | 'PHOTOS' | 'VIDEOS' | 'DOCUMENTS';
type STATUS_FILTER_TYPE = 'ALL' | 'ACCEPT' | 'APPROVE' | 'REJECT';
type CARD_LAYOUT = 2 | 3 | 4;
type Metrics = { cardW: number; mediaH: number; cardH?: number };

const AVAILABLE_PDF_TYPE = [
    'FINAL_INSPECTION_RESULTS',
    'HEAT_LOAD_CALCULATION',
    'PERMIT',
];

export const StyledDownloadIconButton = styled(DownloadIconButton)`
    border-radius: 0 !important;
    padding: 6px 8px !important;
    border: 1px solid #cccccc !important;
`;

interface MediaType {
    id: string;
    display: string;
    displayOrder: number;
}

interface DownloadAllPhotoState {
    state: 'downloading' | 'completed';
    header: string;
    message: string;
    download?: {
        fileName: string;
        url: string;
    };
    timeElapsed?: number;
}

interface ReusedPhotoMetadata {
    refDocumentId: number;
    refDocumentType: string;
}

interface PhotoMetadata {
    hashId: string;
    reused: ReusedPhotoMetadata[];
}

interface DocumentRequirementsPhotosUploadedProps {
    photos?: Photo[];
    photoTypes?: MediaType[];
    videoTypes?: MediaType[];
    disabled?: boolean;
    acls?: boolean;
    removeAcls?: boolean;
    handleSavePhotoOnClick?: (
        id: number,
        typeId: string,
        description: string
    ) => Promise<void>;
    handleRemovePhotoOnClick?: (photoId: string) => Promise<void>;
    handleEditUploadedPhotoRequirement?: (
        photoId: string,
        typeId: string,
        description: string
    ) => Promise<void>;
    handleEditPhotoRequirements?: (
        photoId: string,
        typeId: string,
        description: string
    ) => Promise<void>;
    document?: Document;
    assetTypes?: AssetType[];
    photoTypeRequirements?: PhotoRequirement[];
    handleGetApprovalHistoryPhotoRequirement?: (
        workOrderId: number,
        photoId: number
    ) => Promise<any>;
    handleSaveApprovalPhotoRequirement?: (
        photoId: string,
        approvalStatus: string,
        reason: string
    ) => Promise<void>;
    handleBulkApprovalPhotoRequirement?: (
        ids: string[],
        approval: { approvalStatus: string; reason: string }
    ) => Promise<any>;
    handleEditApprovalPhotoRequirement?: (
        photoId: string,
        approvalStatus: string,
        reason: string
    ) => Promise<void>;
    handleReUploadPhotoOnClick?: (photoId: string, file: File) => Promise<void>;
    handleUploadPhotoOnClick?: (
        photoTypeId: string,
        file: File
    ) => Promise<void>;
    isLegacyUI?: boolean;
    isShowStatusFilter: boolean;
}

const DocumentRequirementsPhotosUploaded = ({
    photos: allPhotos = [],
    document = {} as Document,
    handleBulkApprovalPhotoRequirement = () => Promise.resolve(),
    photoTypeRequirements = [],
    videoTypeRequirements = [],
    photoTypes = [],
    videoTypes = [],
    disabled = false,
    acls = false,
    removeAcls = false,
    handleSavePhotoOnClick: propsHandleSavePhotoOnClick = () =>
        Promise.resolve(),
    handleRemovePhotoOnClick = () => Promise.resolve(),
    handleEditUploadedPhotoRequirement = () => Promise.resolve(),
    handleEditPhotoRequirements = () => Promise.resolve(),
    handleGetApprovalHistoryPhotoRequirement = () => Promise.resolve(),
    handleSaveApprovalPhotoRequirement = () => Promise.resolve(),
    handleEditApprovalPhotoRequirement = () => Promise.resolve(),
    assetTypes = [],
    handleUploadPhotoOnClick = () => Promise.resolve(),
    handleReUploadPhotoOnClick = () => Promise.resolve(),
    isLegacyUI = false,
    isShowStatusFilter = true,
}: DocumentRequirementsPhotosUploadedProps) => {
    const { isMotiliAccount } = useAppSelector(state => state.authentication);
    const { isClientEnablePhotoVerification } = useAppSelector(
        state => state.adminWorkOrder
    );

    const [editPhoto, setEditPhoto] = useState<Photo | null>(null);
    const [isReviewModeOpen, setIsReviewModeOpen] = useState(false);

    const [downloadAllPhoto, setDownloadAllPhoto] =
        useState<DownloadAllPhotoState | null>(null);
    const [recommendPhotoModal, setRecommendPhotoModal] =
        useState<boolean>(false);
    const [selectedPhotoTypeId, setSelectedPhotoTypeId] = useState<
        string | null
    >(null);
    const [refDocs, setRefDocs] = useState<Document[] | null>(null);
    const [selectedBulkApproval, setSelectedBulkApproval] = useState<Photo[]>(
        []
    );
    const [isBulkApproveDrawerOpen, setIsBulkApproveDrawerOpen] =
        useState<boolean>(false);
    const [qualifiedPhotos, setQualifiedPhotos] = useState<Photo[]>([]);
    const [isFromClickedBulkApproval, setIsFromClickedBulkApproval] =
        useState<boolean>(false);
    const [mediaFilterByTypeId, setMediaFilterByTypeId] = useState<{
        [key: string]: MEDIA_FILTER_TYPE;
    }>({});
    const [searchAndFilterList, setSearchAndFilterList] = useState<any[]>([]); // TODO: Define type for searchAndFilterList
    const [showPhotoMetadataModal, setShowPhotoMetadataModal] =
        useState<boolean>(false);
    const [photoMetadataModalStyle, setPhotoMetadataModalStyle] =
        useState<React.CSSProperties | null>(null);
    const [metadata, setMetadata] = useState<PhotoMetadata | null>(null);
    const [photoIntegrity, setPhotoIntegrity] = useState<PhotoIntegrity | null>(
        null
    );
    const [showPhotoIntegrityFailedModal, setShowPhotoIntegrityFailedModal] =
        useState<boolean>(false);
    const [photoIntegrityFailedModalStyle, setPhotoIntegrityFailedModalStyle] =
        useState<React.CSSProperties | null>(null);
    const [showDocumentsPhotoModal, setShowDocumentsPhotoModal] =
        useState<boolean>(false);
    const [photoStatusFilter, setPhotoStatusFilter] =
        useState<STATUS_FILTER_TYPE>();
    const [selectedMediaTypeFilter, setSelectedMediaTypeFilter] = useState();
    const [isFilterSticky, setIsFilterSticky] = useState(false);
    const sentinelFilterStickyRef = useRef(null);

    const CARD_GAP = 16;
    const BASE_CARD_W = 312;
    const BASE_MEDIA_H = 193;
    const [metricsBySection, setMetricsBySection] = useState<
        Record<string, Metrics>
    >({});

    const rowEls = React.useRef<Record<string, HTMLDivElement | null>>({});
    const observers = React.useRef<Record<string, ResizeObserver | undefined>>(
        {}
    );
    const rowRefCbs = React.useRef<
        Record<string, (node: HTMLDivElement | null) => void>
    >({});

    const [cardLayout, setCardLayout] = useState<CARD_LAYOUT>(4);
    const [scrollState, setScrollState] = useState<
        Record<string, { canScrollLeft: boolean; canScrollRight: boolean }>
    >({});
    const [isShowApprovalSummary, setIsShowApprovalSummary] = useState(false);

    const handleCardsRowScroll = useCallback(
        (photoTypeId: string, e: React.UIEvent<HTMLDivElement>) => {
            const el = e.currentTarget;
            const canScrollLeft = el.scrollLeft > 0;
            const canScrollRight =
                el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
            setScrollState(prev => ({
                ...prev,
                [photoTypeId]: { canScrollLeft, canScrollRight },
            }));
        },
        []
    );

    const initScrollState = useCallback(
        (photoTypeId: string, el: HTMLDivElement | null) => {
            if (!el) return;
            const canScrollLeft = el.scrollLeft > 0;
            const canScrollRight =
                el.scrollLeft + el.clientWidth < el.scrollWidth - 1;

            setScrollState(prev => {
                const cur = prev[photoTypeId];
                if (
                    cur &&
                    cur.canScrollLeft === canScrollLeft &&
                    cur.canScrollRight === canScrollRight
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    [photoTypeId]: { canScrollLeft, canScrollRight },
                };
            });
        },
        []
    );

    const cols = cardLayout;

    const calcMetrics = useCallback(
        (containerW: number) => {
            const perView = Number(cardLayout);
            const safeW = Math.min(containerW, window.innerWidth);

            const w = Math.floor((safeW - CARD_GAP * (perView - 1)) / perView);
            const cardW = Math.max(MinCardWidth, w);
            const mediaH = Math.round((cardW * BASE_MEDIA_H) / BASE_CARD_W);

            return { cardW, mediaH };
        },
        [cardLayout, CARD_GAP, BASE_MEDIA_H, BASE_CARD_W]
    );

    const getLayoutIcon = (layout: CARD_LAYOUT, active: boolean) => {
        if (layout === 4) {
            return active ? ImageSizeButton4Active : ImageSizeButton4;
        }

        if (layout === 3) {
            return active ? ImageSizeButton3Active : ImageSizeButton3;
        }

        return active ? ImageSizeButton2Active : ImageSizeButton2;
    };

    const calcMetricsRef = React.useRef(calcMetrics);

    useEffect(() => {
        calcMetricsRef.current = calcMetrics;
    }, [calcMetrics]);

    const getRowRef = (photoTypeId: string) => {
        if (rowRefCbs.current[photoTypeId])
            return rowRefCbs.current[photoTypeId];

        rowRefCbs.current[photoTypeId] = (node: HTMLDivElement | null) => {
            rowEls.current[photoTypeId] = node;
            if (!node) {
                observers.current[photoTypeId]?.disconnect();
                observers.current[photoTypeId] = undefined;
                return;
            }
            observers.current[photoTypeId]?.disconnect();

            const ro = new ResizeObserver(entries => {
                const entry = entries[0];
                if (!entry) return;
                const parent = node.parentElement as HTMLElement | null;

                const visibleW =
                    parent?.clientWidth ||
                    node.clientWidth ||
                    entry.contentRect?.width ||
                    node.getBoundingClientRect().width;

                const next = calcMetricsRef.current(visibleW);

                setMetricsBySection(prev => {
                    const cur = prev[photoTypeId];
                    if (
                        cur &&
                        cur.cardW === next.cardW &&
                        cur.mediaH === next.mediaH
                    )
                        return prev;
                    return { ...prev, [photoTypeId]: next };
                });
            });

            ro.observe(node);
            observers.current[photoTypeId] = ro;
        };

        return rowRefCbs.current[photoTypeId];
    };

    useEffect(() => {
        // force recompute metrics for all mounted rows when layout changes
        Object.entries(rowEls.current).forEach(([photoTypeId, node]) => {
            if (!node) return;
            const w =
                node.parentElement?.clientWidth ||
                node.clientWidth ||
                node.getBoundingClientRect().width;
            const next = calcMetrics(w);

            setMetricsBySection(prev => ({
                ...prev,
                [photoTypeId]: next,
            }));
        });
    }, [cardLayout, calcMetrics]);

    const setQualifiedPhotoIds = useCallback(() => {
        if (!allPhotos || !photoTypeRequirements) return;
        const requiredPhotoTypeIds = _.map(
            photoTypeRequirements,
            'requirement.id'
        );
        const filteredQualifiedPhotos = _.filter(
            allPhotos,
            photo =>
                _.isNil(photo.approvalStatusId) &&
                photo.photoIntegrity?.validIntegrity &&
                _.includes(requiredPhotoTypeIds, photo.requirement?.photoTypeId)
        );
        setQualifiedPhotos(filteredQualifiedPhotos);
    }, [allPhotos, photoTypeRequirements]);

    useEffect(() => {
        const filterStickyObserver = new IntersectionObserver(
            ([entries]) => {
                setIsFilterSticky(!entries.isIntersecting);
            },
            { threshold: [0], rootMargin: '0px' }
        );

        if (sentinelFilterStickyRef.current) {
            filterStickyObserver.observe(sentinelFilterStickyRef.current);
        }

        return () => {
            Object.values(observers.current).forEach(o => o?.disconnect());
            observers.current = {};

            filterStickyObserver.disconnect();
            sentinelFilterStickyRef.current = null;
        };
    }, []);

    useEffect(() => {
        Object.entries(rowEls.current).forEach(([photoTypeId, node]) => {
            if (!node) {
                return;
            }
            const w =
                node.parentElement?.clientWidth ||
                node.clientWidth ||
                node.getBoundingClientRect().width;
            const next = calcMetrics(w);

            setMetricsBySection(prev => {
                const cur = prev[photoTypeId];

                if (
                    cur &&
                    cur.cardW === next.cardW &&
                    cur.mediaH === next.mediaH
                ) {
                    return prev;
                }

                return { ...prev, [photoTypeId]: next };
            });
        });
    }, [cardLayout, calcMetrics]);

    useEffect(() => {
        if (
            isMotiliAccount &&
            document?.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
            document?.statusId === WORK_ORDER_STATUS.IN_REVIEW &&
            isClientEnablePhotoVerification
        ) {
            setQualifiedPhotoIds();
        } else {
            setQualifiedPhotos([]);
            setSelectedBulkApproval([]);
        }

        setIsShowApprovalSummary(
            isMotiliAccount &&
                document?.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
                isClientEnablePhotoVerification
        );
    }, [
        allPhotos,
        document,
        setQualifiedPhotoIds,
        isMotiliAccount,
        isClientEnablePhotoVerification,
    ]);

    const handleShowMetaData = useCallback(
        async (
            metadata: PhotoMetadata,
            style: React.CSSProperties,
            photoIntegrity: PhotoIntegrity
        ) => {
            const reused = await getPhotoMetadataByHashId(metadata.hashId);
            setMetadata({ ...metadata, reused });
            setPhotoIntegrity(photoIntegrity);
            setPhotoMetadataModalStyle(style);
            setShowPhotoMetadataModal(true);
        },
        []
    );

    const handleShowFailIntegrityReason = useCallback(
        async (style: React.CSSProperties, photoIntegrity: PhotoIntegrity) => {
            setPhotoIntegrity(photoIntegrity);
            setPhotoIntegrityFailedModalStyle(style);
            setShowPhotoIntegrityFailedModal(true);
        },
        []
    );

    const handleReusedViewOnClick = useCallback(
        async (metadata: PhotoMetadata) => {
            const docIds = metadata.reused
                .filter(m => m.refDocumentId)
                .map(m => m.refDocumentId);
            let refDocs: Document[] = [];
            if (docIds.length > 0) {
                const docs = await getUsagePhotoByDocIds(docIds);
                refDocs = docs.filter((doc: Document) =>
                    metadata.reused.find(
                        m =>
                            m.refDocumentId === doc.id &&
                            m.refDocumentType === doc.documentTypeId
                    )
                );
            }
            setRefDocs(refDocs);
            setShowPhotoMetadataModal(false);
            setShowDocumentsPhotoModal(true);
        },
        []
    );

    const handleSavePhotoOnClick = useCallback(
        ({ typeId, description }: { typeId: string; description: string }) => {
            if (!editPhoto || !propsHandleSavePhotoOnClick)
                return Promise.resolve();
            return propsHandleSavePhotoOnClick(
                editPhoto.id,
                typeId,
                description
            ).then(() => setEditPhoto(null));
        },
        [editPhoto, propsHandleSavePhotoOnClick]
    );

    const handleDownloadAllPhoto = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const startTime = new Date();

            let downloadUrls: string[] = [];
            let documentTypeHeader = '';
            let documentTypeMessage = '';
            let documentTypeFileName = '';

            if (!document) return;

            if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
                downloadUrls = await QuoteGetDownloadAllPhotoUrl(document.id);
                documentTypeHeader = isLegacyUI ? 'QUOTE' : 'Quote';
                documentTypeMessage = 'Quote';
                documentTypeFileName = 'Quote';
            } else if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
                downloadUrls = await WordOrderGetDownloadAllPhotoUrl(
                    document.id
                );
                documentTypeHeader = isLegacyUI ? 'WORK ORDER' : 'Work Order';
                documentTypeMessage = 'Work Order';
                documentTypeFileName = 'WorkOrder';
            } else {
                return;
            }

            setDownloadAllPhoto({
                state: 'downloading',
                header: `${documentTypeHeader} #${document.number} Zip file is generating`,
                message: '',
            });

            const zip = new jszip();
            const controller = new AbortController();
            const { signal } = controller;

            try {
                const fetchPromises = downloadUrls.map(downloadUrl =>
                    fetch(downloadUrl, { method: 'get', signal }).then(
                        response => response.blob()
                    )
                );

                const blobs = await Promise.all(fetchPromises);
                await Promise.all(blobs.map(blob => zip.loadAsync(blob)));

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                const zipUrl = URL.createObjectURL(zipBlob);
                const endTime = new Date();
                const timeElapsed =
                    (endTime.getTime() - startTime.getTime()) / 1000;

                setDownloadAllPhoto({
                    header: `${documentTypeHeader} #${document.number} Photos Successfully Downloaded`,
                    download: {
                        fileName: `${documentTypeFileName}${document.number}_Photos.zip`,
                        url: zipUrl,
                    },
                    message: `Zip file for ${documentTypeMessage} #${document.number} was successfully generated.`,
                    timeElapsed,
                    state: 'completed',
                });
            } catch (error) {
                logger.error(error);
                controller.abort();
                setDownloadAllPhoto({
                    header: `${documentTypeHeader} #${document.number} Photos Download Failed`,
                    message: `Zip file for ${documentTypeMessage} #${document.number} can't generate. Please contact Admin.`,
                    state: 'completed',
                });
            }
        },
        [document, isLegacyUI]
    );

    const setMediaFilter = useCallback(
        (photoTypeId: string, filter: MEDIA_FILTER_TYPE) => {
            const safeFilter = Object.values(MEDIA_FILTER).includes(filter)
                ? filter
                : (selectedMediaTypeFilter ?? MEDIA_FILTER.ALL);
            setMediaFilterByTypeId(prev => ({
                ...(prev || {}),
                [photoTypeId]: safeFilter,
            }));
        },
        [selectedMediaTypeFilter]
    );

    const handleSearchAndFilterChanged = useCallback(
        (list: ActiveFilterItem[]) => {
            setSearchAndFilterList(list);
            const filterMediaType = _.find(
                list,
                x => x.type === FilterType.MEDIA_TYPE
            )?.value;

            setSelectedMediaTypeFilter(filterMediaType);

            const photoTypeIdList = _.uniq([
                ..._.map(allPhotos, x => x.requirement?.photoTypeId),
                ..._.map(photoTypeRequirements, x => x.requirement?.id),
            ]);
            _.forEach(photoTypeIdList, type => {
                if (type) {
                    setMediaFilter(
                        type,
                        (filterMediaType ??
                            MEDIA_FILTER.ALL) as MEDIA_FILTER_TYPE
                    );
                }
            });

            const filterStatus = _.find(
                list,
                x => x.type === FilterType.STATUS
            )?.value;
            setPhotoStatusFilter(
                (filterStatus ?? FILTER_ALL) as STATUS_FILTER_TYPE
            );
        },
        [allPhotos, photoTypeRequirements, setMediaFilter]
    );

    const { photos, groupedSections } = useMemo(() => {
        const uploadedPhotosOnly: Photo[] = allPhotos || [];

        const requiredTypeIds = new Set(
            [...photoTypeRequirements, ...videoTypeRequirements].map(
                r => r?.requirement?.id
            )
        );

        const blankPhotos = (photoTypeRequirements || [])
            .filter(photoTypeRequirement => {
                const alreadyUploaded = uploadedPhotosOnly.some(
                    photo =>
                        photoTypeRequirement.requirement.id ===
                        photo.requirement.photoTypeId
                );
                return !alreadyUploaded;
            })
            .map(photoTypeRequirement => ({
                id: `blank-${photoTypeRequirement.requirement.id}`, // Assign a unique ID for blank photos
                requirement: {
                    ...photoTypeRequirement.requirement,
                    photoTypeId: photoTypeRequirement.requirement.id,
                },
                requirementTypeId: 'PHOTO',
                created: null,
            }));

        const blankVideos = (videoTypeRequirements || [])
            .filter(videoTypeRequirement => {
                const alreadyUploaded = uploadedPhotosOnly.some(
                    photo =>
                        videoTypeRequirement.requirement.id ===
                        photo.requirement.photoTypeId
                );
                return !alreadyUploaded;
            })
            .map(videoTypeRequirement => ({
                id: `blank-${videoTypeRequirement.requirement.id}`, // Assign a unique ID for blank photos
                requirement: {
                    ...videoTypeRequirement.requirement,
                    photoTypeId: videoTypeRequirement.requirement.id,
                },
                requirementTypeId: 'VIDEO',
                created: null,
            }));

        const sortedPhotos = sortRequirementMedia(
            photoTypes,
            videoTypes,
            allPhotos,
            blankPhotos,
            blankVideos
        );

        const groupByTypeId = (items: Photo[]) => {
            const map = new Map<string, Photo[]>();
            items.forEach(p => {
                const key = p?.requirement?.photoTypeId || 'UNKNOWN';
                map.set(key, [...(map.get(key) || []), p]);
            });

            const filterPhotoTypeList = _.chain(searchAndFilterList)
                .filter(item => item.type === FilterType.PHOTO_TYPE)
                .map(item => item.value)
                .value();

            if (!_.isEmpty(filterPhotoTypeList)) {
                _.chain(Array.from(map.keys()))
                    .filter(key => !filterPhotoTypeList.includes(key))
                    .forEach(key => map.delete(key))
                    .value();
            }

            const getRequirementType = (typeId: string): string => {
                if (videoTypes.find(t => t.id === typeId)) {
                    return 'VIDEO';
                }
                return 'PHOTO';
            };

            // filter media type
            _.forEach(Array.from(map.keys()), photoTypeId => {
                const mediaFilterValue = (mediaFilterByTypeId || {})[
                    photoTypeId
                ];
                if (
                    mediaFilterValue &&
                    mediaFilterValue !== FILTER_ALL &&
                    !_.some(blankPhotos, b => b.requirement.id === photoTypeId)
                ) {
                    const photoList = map.get(photoTypeId);
                    const isVideoGroup =
                        getRequirementType(photoTypeId) === 'VIDEO';

                    let visibleItems: Photo[] = [];
                    if (mediaFilterValue === MEDIA_FILTER.PHOTOS) {
                        visibleItems = _.filter(photoList, p =>
                            isVideoGroup
                                ? p.requirementTypeId === 'PHOTO'
                                : isPhoto(p)
                        );
                    } else if (mediaFilterValue === MEDIA_FILTER.VIDEOS) {
                        visibleItems = _.filter(photoList, p =>
                            isVideoGroup
                                ? p.requirementTypeId === 'VIDEO'
                                : isVideo(p)
                        );
                    } else if (mediaFilterValue === MEDIA_FILTER.DOCUMENTS) {
                        visibleItems = _.filter(photoList, p => isPDF(p));
                    }
                    map.set(photoTypeId, visibleItems);
                }
            });

            // filter status
            _.forEach(Array.from(map.keys()), photoTypeId => {
                if (photoStatusFilter && photoStatusFilter !== FILTER_ALL) {
                    const photoList = map.get(photoTypeId);
                    const visibleItems = _.filter(
                        photoList,
                        item => item.approvalStatusId === photoStatusFilter
                    );
                    map.set(photoTypeId, visibleItems);
                }
            });

            return [...map.entries()].map(([typeId, groupItems]) => ({
                typeId,
                title: getMediaTypeDisplay(typeId, photoTypes, videoTypes),
                items: groupItems,
                isMet:
                    requiredTypeIds.has(typeId) &&
                    isRequirementMet(typeId, uploadedPhotosOnly),
                requirementTypeId: getRequirementType(typeId),
            }));
        };

        return {
            photos: sortedPhotos,
            groupedSections: groupByTypeId(sortedPhotos),
        };
    }, [
        allPhotos,
        photoTypeRequirements,
        photoTypes,
        searchAndFilterList,
        mediaFilterByTypeId,
        photoStatusFilter,
        videoTypes,
        videoTypeRequirements,
    ]);
    const hasUploadedMedia = useMemo(
        () => _.some(photos, p => p?.requirement?.url),
        [photos]
    );
    const canShowLayoutToggle =
        (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER ||
            document.documentTypeId === DOCUMENT_TYPE.QUOTE) &&
        isMotiliAccount &&
        photos.some(p => Boolean(p?.requirement?.mimeType));

    // eligible = qualified + currently visible by search/filter (groupedSections)
    const visibleQualifiedPhotos = useMemo(() => {
        const qualifiedIds = new Set((qualifiedPhotos || []).map(p => p.id));
        const visible: Photo[] = [];

        (groupedSections || []).forEach(section => {
            (section.items || []).forEach((p: Photo) => {
                if (
                    p?.id &&
                    p?.requirement?.mimeType &&
                    qualifiedIds.has(p.id)
                ) {
                    visible.push(p);
                }
            });
        });

        return _.uniqBy(visible, 'id');
    }, [qualifiedPhotos, groupedSections]);

    const isAllVisibleQualifiedSelected = useMemo(() => {
        if (_.isEmpty(visibleQualifiedPhotos)) return false;
        const selectedIds = new Set(
            (selectedBulkApproval || []).map(p => p.id)
        );
        return visibleQualifiedPhotos.every(p => selectedIds.has(p.id));
    }, [visibleQualifiedPhotos, selectedBulkApproval]);

    const handleToggleSelectAllApplicablePhotos = useCallback(() => {
        if (isAllVisibleQualifiedSelected) {
            const visibleIds = new Set(visibleQualifiedPhotos.map(p => p.id));
            setSelectedBulkApproval(prev =>
                (prev || []).filter(p => !visibleIds.has(p.id))
            );
            return;
        }
        setSelectedBulkApproval(prev => {
            const current = prev || [];
            const map = new Map<string, Photo>();
            current.forEach(p => map.set(p.id, p));
            visibleQualifiedPhotos.forEach(p => map.set(p.id, p));
            return Array.from(map.values());
        });

        setIsFromClickedBulkApproval(true);
    }, [isAllVisibleQualifiedSelected, visibleQualifiedPhotos]);

    const photoRequirementMet = useCallback(
        (p: Photo) => {
            return (
                (photoTypeRequirements.some(
                    r => r.requirement?.id === p?.requirement?.photoTypeId
                ) &&
                    Boolean(p?.requirement?.photo || p?.requirement?.url)) ||
                false
            );
        },
        [photoTypeRequirements]
    );

    const videoRequirementMet = useCallback(
        (p: Photo) => {
            return (
                (videoTypeRequirements.some(
                    r => r.requirement?.id === p?.requirement?.photoTypeId
                ) &&
                    Boolean(p?.requirement?.video || p?.requirement?.url)) ||
                false
            );
        },
        [videoTypeRequirements]
    );

    const APPROVAL_STATUS_IDS = Object.values(APPROVAL_STATUS).map(s => s.id);
    // => ['APPROVE','REJECT','ACCEPT']

    const hasApprovalStatus = (photo: Photo) => {
        return APPROVAL_STATUS_IDS.includes(photo.approvalStatusId ?? '');
    };

    const isRejected = (p: Photo) => {
        return (p.approvalStatusId ?? '') === APPROVAL_STATUS.REJECT.id;
    };

    const reviewItems = useMemo(
        () => getReviewItems(groupedSections),
        [groupedSections]
    );

    const canShowReviewMode =
        document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
        isMotiliAccount &&
        document.statusId === WORK_ORDER_STATUS.IN_REVIEW &&
        reviewItems.some(
            photo =>
                photoRequirementMet(photo) &&
                checkShowApprovalRejectButton(
                    photo,
                    document,
                    isRejected,
                    isClientEnablePhotoVerification
                ) &&
                !hasApprovalStatus(photo)
        );

    const isShowDocumentMediaType = useMemo(() => {
        return _.some(
            photos,
            photo =>
                AVAILABLE_PDF_TYPE.includes(photo.photoMetadata?.photoTypeId) ||
                AVAILABLE_PDF_TYPE.includes(photo.requirement?.photoTypeId)
        );
    }, [photos]);

    const getUploadedMediaCounts = useCallback(
        (photoTypeId: string) => {
            const uploaded = (allPhotos || []).filter(
                p =>
                    p?.requirement?.photoTypeId === photoTypeId &&
                    _.isString(p?.requirement?.mimeType)
            );
            const photosCount = uploaded.filter(p => !isVideo(p)).length;
            const videosCount = uploaded.length - photosCount;
            return { total: uploaded.length, photosCount, videosCount };
        },
        [allPhotos]
    );

    const isEmptySectionBySearch = useCallback(
        section => {
            return (
                _.isEmpty(section.items) &&
                (!_.isEmpty(mediaFilterByTypeId) ||
                    !_.isEmpty(photoStatusFilter))
            );
        },
        [mediaFilterByTypeId, photoStatusFilter]
    );

    const isEnabledSearchAndFilter = useMemo(() => {
        return _.some(
            groupedSections,
            section =>
                isEmptySectionBySearch(section) ||
                _.some(
                    section.items,
                    item => !_.isNil(item.requirement?.mimeType)
                )
        );
    }, [groupedSections, isEmptySectionBySearch]);

    useEffect(() => {
        // Set media filter as selected media type and delete media type list when there is no photo, to be able to set the selected type again
        _.chain(groupedSections)
            .filter(section => !(mediaFilterByTypeId || {})[section.typeId])
            .forEach(section => {
                setMediaFilter(
                    section.typeId,
                    (selectedMediaTypeFilter ??
                        MEDIA_FILTER.ALL) as MEDIA_FILTER_TYPE
                );
            })
            .value();

        _.forEach(_.keys(mediaFilterByTypeId), mediaFilter => {
            if (
                !_.some(
                    groupedSections,
                    section => section.typeId === mediaFilter
                )
            ) {
                delete mediaFilterByTypeId[mediaFilter];
            }
        });
    }, [
        groupedSections,
        mediaFilterByTypeId,
        selectedMediaTypeFilter,
        selectedPhotoTypeId,
        setMediaFilter,
    ]);

    const deSelectBulkApprovalList = photoId => {
        const newBulkList = selectedBulkApproval.filter(s => s.id !== photoId);
        setSelectedBulkApproval(newBulkList);
    };

    const stickyStyle = useMemo(() => {
        const stickyActiveStyle = {
            boxShadow: '0 8px 8px -8px rgba(0, 0, 0, 0.25)',
            marginLeft: '-25px',
            marginRight: '-25px',
            paddingLeft: '25px',
            paddingRight: '25px',
        };
        const style = {
            position: 'sticky',
            top: 0,
            backgroundColor: Config.color.white,
            zIndex: 9,
            paddingBottom: _.isEmpty(visibleQualifiedPhotos)
                ? '16px'
                : undefined,
        };
        return (
            isFilterSticky ? { ...style, ...stickyActiveStyle } : style
        ) as CSSProperties;
    }, [isFilterSticky, visibleQualifiedPhotos]);

    const approvalSummary = useMemo(() => {
        if (isShowApprovalSummary) {
            const requiredPhotoTypeIds = _.map(
                photoTypeRequirements,
                'requirement.id'
            );
            const countStatus = (status?: MEDIA_STATUS_TYPE) => {
                const condition = (status?: MEDIA_STATUS_TYPE) => {
                    if (_.isNil(status)) {
                        return (approvalStatusId?: string) =>
                            _.isNil(approvalStatusId);
                    }
                    return (approvalStatusId?: string) =>
                        approvalStatusId === status;
                };
                return _.chain(allPhotos)
                    .filter(
                        photo =>
                            _.includes(
                                requiredPhotoTypeIds,
                                photo.requirement?.photoTypeId
                            ) &&
                            !isVideo(photo) &&
                            condition(status)(photo?.approvalStatusId)
                    )
                    .size()
                    .value();
            };
            const needAttention = countStatus();
            const approved = countStatus(MEDIA_STATUS_TYPE.APPROVE);
            const rejected = countStatus(MEDIA_STATUS_TYPE.REJECT);
            const accepted = countStatus(MEDIA_STATUS_TYPE.ACCEPT);
            if (
                needAttention === 0 &&
                approved === 0 &&
                rejected === 0 &&
                accepted === 0
            ) {
                return null;
            }
            return {
                needAttention,
                approved,
                rejected,
                accepted,
            };
        }
        return null;
    }, [allPhotos, isShowApprovalSummary, photoTypeRequirements]);

    const renderDownloadAllPhotosModal = () => {
        if (!downloadAllPhoto) return null;

        if (isLegacyUI) {
            return (
                <DocumentSuccessfullyDownloadAllPhotoModalLegacy
                    handleCloseOnClick={() => setDownloadAllPhoto(null)}
                    header={
                        <div>
                            <div>{downloadAllPhoto.header}</div>
                            {downloadAllPhoto.state === 'completed' &&
                                downloadAllPhoto.download && (
                                    <div
                                        style={{
                                            fontSize: 14,
                                            fontStyle: 'italic',
                                            paddingTop: 5,
                                            fontWeight: 'normal',
                                        }}
                                    >
                                        Zip file created in{' '}
                                        <b>{downloadAllPhoto.timeElapsed}</b>{' '}
                                        seconds
                                    </div>
                                )}
                        </div>
                    }
                    download={downloadAllPhoto.download}
                    message={downloadAllPhoto.message}
                    state={downloadAllPhoto.state}
                />
            );
        }

        return (
            <DocumentSuccessfullyDownloadAllPhotoModal
                handleCloseOnClick={() => setDownloadAllPhoto(null)}
                header={<div>{downloadAllPhoto.header}</div>}
                download={downloadAllPhoto.download}
                message={downloadAllPhoto.message}
                state={downloadAllPhoto.state}
                footerMessage={
                    downloadAllPhoto.state === 'completed' &&
                    downloadAllPhoto.download &&
                    `Zip file created in ${downloadAllPhoto.timeElapsed} seconds`
                }
            />
        );
    };

    return (
        <>
            <DocumentRequirementsRecommendationDrawer
                photoTypeId={selectedPhotoTypeId}
                photoTypes={photoTypes}
                videoTypes={videoTypes}
                handleCloseOnClick={() => setRecommendPhotoModal(false)}
                open={recommendPhotoModal}
            />

            <DocumentRequirementsPhotosReviewModeModal
                open={isReviewModeOpen}
                onClose={() => setIsReviewModeOpen(false)}
                items={reviewItems}
                photosAll={allPhotos}
                document={document}
                photoTypes={photoTypes}
                photoTypeRequirements={photoTypeRequirements}
                photos={photos}
                photoRequirementMet={photoRequirementMet}
                videoRequirementMet={videoRequirementMet}
                hasApprovalStatus={hasApprovalStatus}
                isRejected={isRejected}
                authenticationData={{
                    authenticatedUserCanEdit: true,
                    authenticatedUserIsCon: false,
                }}
                handleSaveApprovalPhotoRequirement={async (
                    photoId,
                    approval
                ) => {
                    if (handleSaveApprovalPhotoRequirement) {
                        await handleSaveApprovalPhotoRequirement(
                            photoId,
                            approval
                        );
                        deSelectBulkApprovalList(photoId);
                    }
                }}
                handleEditUploadedPhotoRequirement={
                    handleEditUploadedPhotoRequirement
                }
                handleRemovePhotoOnClick={async photo => {
                    await handleRemovePhotoOnClick(String(photo.id));
                    deSelectBulkApprovalList(photo.id);
                }}
                handleGetApprovalHistoryPhotoRequirement={
                    handleGetApprovalHistoryPhotoRequirement
                }
                isClientEnablePhotoVerification={
                    isClientEnablePhotoVerification
                }
            />

            {editPhoto && (
                <DocumentRequirementsPhotosEditModal
                    handleCloseOnClick={() => setEditPhoto(null)}
                    handleCancelOnClick={() => setEditPhoto(null)}
                    handleSaveOnClick={handleSavePhotoOnClick}
                    photoTypes={photoTypes}
                    photo={editPhoto}
                    fileName={editPhoto.fileName}
                    imgSrc={editPhoto.url}
                    mimeType={editPhoto.mimeType}
                />
            )}

            {renderDownloadAllPhotosModal()}
            <div ref={sentinelFilterStickyRef} />
            <div style={stickyStyle}>
                <FormRow>
                    <FormRow style={{ marginTop: 24, marginBottom: 0 }}>
                        <FormElement
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            <UploadedPhotosLabel>
                                Uploaded Media
                            </UploadedPhotosLabel>
                        </FormElement>
                    </FormRow>
                    <FormElement
                        style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-end',
                        }}
                    >
                        {canShowLayoutToggle && (
                            <LayoutToggleWrap>
                                <LayoutBtn
                                    type='button'
                                    active={cardLayout === 4}
                                    onClick={() => setCardLayout(4)}
                                    aria-label='4 column layout'
                                    title='4 column layout'
                                >
                                    <img
                                        src={getLayoutIcon(4, cardLayout === 4)}
                                        alt='4 column layout'
                                    />
                                </LayoutBtn>

                                <LayoutBtn
                                    type='button'
                                    active={cardLayout === 3}
                                    onClick={() => setCardLayout(3)}
                                    aria-label='3 column layout'
                                    title='3 column layout'
                                >
                                    <img
                                        src={getLayoutIcon(3, cardLayout === 3)}
                                        alt='3 column layout'
                                    />
                                </LayoutBtn>

                                <LayoutBtn
                                    type='button'
                                    active={cardLayout === 2}
                                    onClick={() => setCardLayout(2)}
                                    aria-label='2 column layout'
                                    title='2 column layout'
                                >
                                    <img
                                        src={getLayoutIcon(2, cardLayout === 2)}
                                        alt='2 column layout'
                                    />
                                </LayoutBtn>
                            </LayoutToggleWrap>
                        )}
                        {canShowReviewMode && reviewItems.length > 0 && (
                            <ReviewModeBtn
                                type='button'
                                onClick={() => setIsReviewModeOpen(true)}
                                aria-label='Review Mode'
                                title='Review Mode'
                                style={{
                                    marginLeft: 10,
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: '1px solid #cccccc',
                                    background: '#fff',
                                    cursor: 'pointer',
                                }}
                            >
                                <img
                                    src={ReviewModeIconImg}
                                    alt='Review Mode'
                                />
                            </ReviewModeBtn>
                        )}
                        {hasUploadedMedia && (
                            <StyledDownloadIconButton
                                style={{
                                    marginLeft: 10,
                                    padding: '6px 10px',
                                }}
                                data-test-id='download-photo-button'
                                iconColor={Config.color.navy}
                                onClick={handleDownloadAllPhoto}
                            />
                        )}
                    </FormElement>
                </FormRow>
                {isShowApprovalSummary && approvalSummary && (
                    <div>
                        [{approvalSummary.needAttention}] Need Attention{' '}
                        <SeparateText /> [{approvalSummary.approved}] Approved{' '}
                        <SeparateText /> [{approvalSummary.rejected}] Rejected{' '}
                        <SeparateText /> [{approvalSummary.accepted}] Accepted
                    </div>
                )}
                {!_.isEmpty(photos) && (
                    <FormRow
                        style={{
                            marginTop: 16,
                        }}
                    >
                        <FormElement>
                            <DocumentRequirementsPhotoSearchAndFilter
                                mediaList={photos}
                                onSearchAndFilterChanged={
                                    handleSearchAndFilterChanged
                                }
                                disabled={!isEnabledSearchAndFilter}
                                isShowStatusFilter={isShowStatusFilter}
                                isShowDocumentFilter={isShowDocumentMediaType}
                            />
                        </FormElement>
                    </FormRow>
                )}
                {(!_.isEmpty(visibleQualifiedPhotos) ||
                    !_.isEmpty(selectedBulkApproval)) && (
                    <FormRow style={{ marginTop: 0 }}>
                        <FormElement>
                            <BulkApproveBar>
                                <BulkApproveLeft>
                                    {!_.isEmpty(visibleQualifiedPhotos) && (
                                        <SelectAllLabel>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Checkbox
                                                    checked={
                                                        isAllVisibleQualifiedSelected
                                                    }
                                                    onChange={() =>
                                                        handleToggleSelectAllApplicablePhotos()
                                                    }
                                                />
                                            </div>
                                            <span
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 'normal',
                                                }}
                                            >
                                                Select all applicable photos
                                            </span>
                                        </SelectAllLabel>
                                    )}
                                    {!_.isEmpty(selectedBulkApproval) && (
                                        <BulkApproveAction
                                            type='button'
                                            onClick={() => {
                                                setIsFromClickedBulkApproval(
                                                    true
                                                );
                                                setIsBulkApproveDrawerOpen(
                                                    true
                                                );
                                            }}
                                        >
                                            <BulkApproveIcon
                                                src={DoneAll}
                                                alt=''
                                            />
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                BULK ACTION
                                            </span>
                                        </BulkApproveAction>
                                    )}
                                </BulkApproveLeft>
                                <BulkApproveSpacer />
                            </BulkApproveBar>
                        </FormElement>
                    </FormRow>
                )}
            </div>
            {showPhotoMetadataModal && (
                <PhotoMetadataModal
                    handleCloseOnClick={() => setShowPhotoMetadataModal(false)}
                    handleReusedViewOnClick={handleReusedViewOnClick}
                    metadata={metadata}
                    photoIntegrity={photoIntegrity}
                    document={document}
                    style={photoMetadataModalStyle}
                />
            )}

            {showPhotoIntegrityFailedModal && (
                <PhotoIntegrityFailReasons
                    handleCloseOnClick={() =>
                        setShowPhotoIntegrityFailedModal(false)
                    }
                    photoIntegrity={photoIntegrity}
                    style={photoIntegrityFailedModalStyle}
                />
            )}

            {showDocumentsPhotoModal && (
                <DocumentsPhotoModal
                    handleCloseOnClick={() => setShowDocumentsPhotoModal(false)}
                    documents={refDocs}
                    metadata={metadata}
                />
            )}

            <AdminBulkApprovalSideBar
                isOpen={isBulkApproveDrawerOpen}
                photoSelected={selectedBulkApproval}
                onClose={() => {
                    setIsBulkApproveDrawerOpen(false);
                    setIsFromClickedBulkApproval(false);
                }}
                isFromClickedBulkApproval={isFromClickedBulkApproval}
                handleBulkApprovalPhotoRequirement={async (ids, approval) => {
                    if (!handleBulkApprovalPhotoRequirement)
                        return Promise.resolve();
                    const res = await handleBulkApprovalPhotoRequirement(
                        ids,
                        approval
                    );
                    setIsBulkApproveDrawerOpen(false);
                    setSelectedBulkApproval([]);
                    setIsFromClickedBulkApproval(false);

                    return res;
                }}
            />

            {_.isEmpty(photos) && (
                <div
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: Config.color.mediumGray,
                        marginTop: 16,
                    }}
                >
                    No media requirements have been added to this document.
                </div>
            )}
            {groupedSections.map(section => {
                const metrics = metricsBySection[section.typeId] ?? {
                    cardW: BASE_CARD_W,
                    mediaH: BASE_MEDIA_H,
                    cardH: undefined,
                };

                const counts = getUploadedMediaCounts(section.typeId);
                const scroll = scrollState[section.typeId] ?? {
                    canScrollLeft: false,
                    canScrollRight: false,
                };

                return (
                    <div key={section.typeId}>
                        <RequirementHeaderRow>
                            <RequirementHeaderLeft>
                                <HeaderInfoIcon
                                    onClick={() => {
                                        setRecommendPhotoModal(true);
                                        setSelectedPhotoTypeId(section.typeId);
                                    }}
                                />
                                <RequirementTitle>
                                    {section.title}
                                </RequirementTitle>
                                {section.isMet && <HeaderCheckIcon />}
                            </RequirementHeaderLeft>
                            {counts.total > 0 && (
                                <MediaTabs
                                    photoTypeId={section.typeId}
                                    currentFilter={
                                        (mediaFilterByTypeId || {})[
                                            section.typeId
                                        ] || MEDIA_FILTER.ALL
                                    }
                                    onSetFilter={setMediaFilter}
                                    isShowDocument={isShowDocumentMediaType}
                                />
                            )}
                        </RequirementHeaderRow>
                        <ScrollFadeContainer
                            $fadeLeft={scroll.canScrollLeft}
                            $fadeRight={scroll.canScrollRight}
                        >
                            <CardsRow
                                $cols={cols}
                                ref={node => {
                                    getRowRef(section.typeId)(node);
                                    initScrollState(section.typeId, node);
                                }}
                                onScroll={e =>
                                    handleCardsRowScroll(section.typeId, e)
                                }
                            >
                                {isEmptySectionBySearch(section) &&
                                    counts.total > 0 && (
                                        <Flex>
                                            No results found. Please refine your
                                            search and try again
                                        </Flex>
                                    )}
                                {section.items.map(photo => {
                                    const hasPhoto = Boolean(
                                        photo?.requirement?.mimeType
                                    );
                                    const key =
                                        photo.id ||
                                        `${section.typeId}-${photo?.requirement?.id || ''}`;
                                    const isSelectableForBulk =
                                        Boolean(photo?.requirement?.mimeType) &&
                                        !isVideo(photo) &&
                                        !hasApprovalStatus(photo) &&
                                        photoRequirementMet(photo);
                                    const content = (
                                        <DocumentRequirementsPhotosUploadedRowContainer
                                            cardWidth={metrics.cardW}
                                            mediaHeight={metrics.mediaH}
                                            cardHeight={metrics.cardH}
                                            photos={allPhotos || []}
                                            photo={photo}
                                            photoTypeRequirements={
                                                photoTypeRequirements || []
                                            }
                                            videoTypeRequirements={
                                                videoTypeRequirements || []
                                            }
                                            requirementTypeId={
                                                section.requirementTypeId
                                            }
                                            quotePhotoTypeRequirements={photos}
                                            photoTypes={photoTypes}
                                            videoTypes={videoTypes}
                                            disabled={disabled}
                                            acls={acls}
                                            removeAcls={removeAcls}
                                            handleEditPhotoOnClick={() =>
                                                setEditPhoto(photo)
                                            }
                                            handleRemovePhotoOnClick={async photoId => {
                                                if (handleRemovePhotoOnClick) {
                                                    await handleRemovePhotoOnClick(
                                                        photoId
                                                    );
                                                    deSelectBulkApprovalList(
                                                        photoId
                                                    );
                                                }
                                            }}
                                            handleEditUploadedPhotoRequirement={async (
                                                photoId,
                                                typeId,
                                                description
                                            ) => {
                                                if (
                                                    handleEditUploadedPhotoRequirement
                                                ) {
                                                    await handleEditUploadedPhotoRequirement(
                                                        photoId,
                                                        typeId,
                                                        description
                                                    );
                                                    deSelectBulkApprovalList(
                                                        photoId
                                                    );
                                                }
                                            }}
                                            handleEditPhotoRequirements={
                                                handleEditPhotoRequirements
                                            }
                                            handleShowMetaData={
                                                handleShowMetaData
                                            }
                                            handleShowFailIntegrityReason={
                                                handleShowFailIntegrityReason
                                            }
                                            handleGetApprovalHistoryPhotoRequirement={
                                                handleGetApprovalHistoryPhotoRequirement
                                            }
                                            handleSaveApprovalPhotoRequirement={async (
                                                photoId,
                                                approval
                                            ) => {
                                                if (
                                                    handleSaveApprovalPhotoRequirement
                                                ) {
                                                    await handleSaveApprovalPhotoRequirement(
                                                        photoId,
                                                        approval
                                                    );
                                                    deSelectBulkApprovalList(
                                                        photoId
                                                    );
                                                }
                                            }}
                                            handleEditApprovalPhotoRequirement={
                                                handleEditApprovalPhotoRequirement
                                            }
                                            document={document}
                                            assetTypes={assetTypes}
                                            handleUploadPhotoRequirement={
                                                handleUploadPhotoOnClick
                                            }
                                            handleReUploadRejectedPhotoRequirement={
                                                handleReUploadPhotoOnClick
                                            }
                                            openRecommendationModal={(
                                                photoTypeId: string
                                            ) => {
                                                setRecommendPhotoModal(true);
                                                setSelectedPhotoTypeId(
                                                    photoTypeId
                                                );
                                            }}
                                            selectPhotoBulkApproval={
                                                isSelectableForBulk
                                                    ? () => {
                                                          setIsFromClickedBulkApproval(
                                                              false
                                                          );
                                                          const result =
                                                              selectedBulkApproval.find(
                                                                  s =>
                                                                      s.id ===
                                                                      photo.id
                                                              )
                                                                  ? selectedBulkApproval.filter(
                                                                        s =>
                                                                            s.id !==
                                                                            photo.id
                                                                    )
                                                                  : [
                                                                        ...selectedBulkApproval,
                                                                        photo,
                                                                    ];
                                                          setSelectedBulkApproval(
                                                              result
                                                          );
                                                      }
                                                    : undefined
                                            }
                                            bulkApprovalSelected={
                                                isSelectableForBulk
                                                    ? Boolean(
                                                          selectedBulkApproval.find(
                                                              s =>
                                                                  s.id ===
                                                                  photo.id
                                                          )
                                                      )
                                                    : undefined
                                            }
                                        />
                                    );

                                    return hasPhoto ? (
                                        <CardItem
                                            key={key}
                                            $cardW={metrics.cardW}
                                            $cardH={metrics.cardH}
                                        >
                                            {content}
                                        </CardItem>
                                    ) : (
                                        <EmptyCardItem key={key}>
                                            {content}
                                        </EmptyCardItem>
                                    );
                                })}
                            </CardsRow>
                        </ScrollFadeContainer>
                    </div>
                );
            })}
        </>
    );
};

export const getReviewItems = (
    groupedSections: { items: Photo[] }[]
): Photo[] => {
    const result: Photo[] = [];
    groupedSections.forEach(section => {
        section.items.forEach((p: Photo) => {
            const hasUploadedFile = Boolean(
                p?.requirement?.photo || p?.requirement?.url
            );
            const status =
                p?.approvalStatusId ??
                (p?.requirement as any)?.approvalStatusId;
            const noStatusYet = _.isNil(status);
            if (hasUploadedFile && noStatusYet && !isVideo(p)) {
                result.push(p);
            }
        });
    });
    return result;
};

export default DocumentRequirementsPhotosUploaded;

export const isVideo = (p: Photo): boolean =>
    isVideoMimeType(p?.requirement?.mimeType || p?.mimeType || '');

export const isPhoto = (p: Photo): boolean =>
    isImageMimeType(p?.requirement?.mimeType || p?.mimeType || '');

export const isPDF = (p: Photo): boolean =>
    isPdfMimeType(p?.requirement?.mimeType || p?.mimeType || '');

const TabBtn = ({
    id,
    label,
    onClick,
    current,
}: {
    id: MEDIA_FILTER_TYPE;
    label: string;
    onClick: () => void;
    current: MEDIA_FILTER_TYPE;
}) => (
    <span
        style={{
            padding: '6px 10px',
            border: '1px solid #e0e0e0',
            borderRight:
                id !== MEDIA_FILTER.VIDEOS ? 'none' : '1px solid #e0e0e0',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            background: current === id ? '#57c1e9' : '#fff',
            color: current === id ? '#fff' : '#333',
            userSelect: 'none',
        }}
        onClick={onClick}
    >
        {label}
    </span>
);

const MediaTabs = ({
    photoTypeId,
    currentFilter,
    onSetFilter,
    isShowDocument,
}: {
    photoTypeId: string;
    currentFilter: MEDIA_FILTER_TYPE;
    onSetFilter: (photoTypeId: string, filter: MEDIA_FILTER_TYPE) => void;
    isShowDocument: boolean;
}) => (
    <div
        style={{
            display: 'inline-flex',
            overflow: 'hidden',
        }}
    >
        <TabBtn
            id={MEDIA_FILTER.ALL}
            label='All'
            onClick={() => onSetFilter(photoTypeId, MEDIA_FILTER.ALL)}
            current={currentFilter}
        />
        {isShowDocument && (
            <TabBtn
                id={MEDIA_FILTER.DOCUMENTS}
                label='Documents'
                onClick={() => onSetFilter(photoTypeId, MEDIA_FILTER.DOCUMENTS)}
                current={currentFilter}
            />
        )}
        <TabBtn
            id={MEDIA_FILTER.PHOTOS}
            label='Photos'
            onClick={() => onSetFilter(photoTypeId, MEDIA_FILTER.PHOTOS)}
            current={currentFilter}
        />
        <TabBtn
            id={MEDIA_FILTER.VIDEOS}
            label='Videos'
            onClick={() => onSetFilter(photoTypeId, MEDIA_FILTER.VIDEOS)}
            current={currentFilter}
        />
    </div>
);

export const getMediaTypeDisplay = (
    typeId: string,
    photoTypes: MediaType[] = [],
    videoTypes: MediaType[] = []
): string => {
    const pt = [...photoTypes, ...videoTypes].find(t => t.id === typeId);
    return pt?.display || typeId || 'Unknown';
};

export const photosSequenceSortFunc = (a: Photo, b: Photo) =>
    new Date(a.created) - new Date(b.created) || a.id - b.id;

export const sortRequirementMedia = (
    photoTypes: MediaType[],
    videoTypes: MediaType[],
    allPhotos: Photo[],
    blankPhotos: Photo[] = [],
    blankVideos: Photo[] = []
): Photo[] => {
    const getMimeTypeOrder = (photo: Photo) => {
        const mimeType = photo?.requirement?.mimeType;
        if (isPdfMimeType(mimeType)) return 1;
        if (isImageMimeType(mimeType)) return 2;
        if (isVideoMimeType(mimeType)) return 3;

        return Number.MAX_VALUE;
    };

    const getDisplayOrder = (photo: Photo): number => {
        const MEDIA_TYPES = [...photoTypes, ...videoTypes];
        if (!photo.requirement?.photoTypeId || MEDIA_TYPES.length === 0) {
            return Number.MAX_VALUE;
        }
        const photoType = MEDIA_TYPES.find(
            p => p.id === photo.requirement?.photoTypeId
        );

        return photoType?.displayOrder ?? Number.MAX_VALUE;
    };

    const photosWithBlanks = [
        ...allPhotos.sort(photosSequenceSortFunc),
        ...blankPhotos,
        ...blankVideos,
    ].map(p => ({
        ...p,
        requirementTypeOrder: p.requirementTypeId === 'VIDEO' ? 1 : 2,
        displayOrder: getDisplayOrder(p),
        mimeTypeOrder: getMimeTypeOrder(p),
    }));

    return _.orderBy(photosWithBlanks, [
        'requirementTypeOrder',
        'displayOrder',
        'mimeTypeOrder',
    ]);
};
